import { format, differenceInDays, isAfter, isBefore } from 'date-fns';
import { zhTW } from 'date-fns/locale';
import { TaskStatus } from '../types';

// 格式化日期
export const formatDate = (date: Date | string | undefined, formatStr: string = 'yyyy/MM/dd'): string => {
  if (!date) return '-';
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return format(dateObj, formatStr, { locale: zhTW });
};

// 格式化短日期
export const formatShortDate = (date: Date | string | undefined): string => {
  if (!date) return '-';
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return format(dateObj, 'MM/dd', { locale: zhTW });
};

// 計算剩餘天數
export const calculateDaysRemaining = (targetDate: Date | string): number => {
  const target = typeof targetDate === 'string' ? new Date(targetDate) : targetDate;
  return differenceInDays(target, new Date());
};

// 檢查是否逾期
export const isOverdue = (expectedDate: Date | string, status: TaskStatus): boolean => {
  if (status === 'completed') return false;
  const expected = typeof expectedDate === 'string' ? new Date(expectedDate) : expectedDate;
  return isBefore(expected, new Date());
};

// 計算預期進度
export const calculateExpectedProgress = (startDate: Date, endDate: Date): number => {
  const now = new Date();
  const totalDays = differenceInDays(endDate, startDate);
  const elapsedDays = differenceInDays(now, startDate);
  
  if (elapsedDays <= 0) return 0;
  if (elapsedDays >= totalDays) return 100;
  
  return Math.round((elapsedDays / totalDays) * 100);
};

// 取得狀態顯示資訊
export const getStatusInfo = (status: TaskStatus) => {
  const statusMap = {
    'pending': { icon: '○', text: '待執行', color: '#e2e8f0', textColor: '#a0aec0' },
    'in-progress': { icon: '◐', text: '執行中', color: '#4299e1', textColor: 'white' },
    'pending-review': { icon: '◈', text: '待驗收', color: '#f6ad55', textColor: 'white' },
    'reviewing': { icon: '◉', text: '驗收中', color: '#9f7aea', textColor: 'white' },
    'review-failed': { icon: '✗', text: '驗收失敗', color: '#e53e3e', textColor: 'white' },
    'completed': { icon: '✓', text: '已完成', color: '#48bb78', textColor: 'white' }
  };
  
  return statusMap[status] || statusMap.pending;
};

// 數字轉中文
export const numberToChinese = (num: number): string => {
  // Handle undefined, null, or invalid numbers
  if (num == null || isNaN(num)) {
    return '';
  }
  
  const chinese = ['', '一', '二', '三', '四', '五', '六', '七', '八', '九', '十'];
  if (num <= 10) {
    return chinese[num];
  } else {
    return num.toString();
  }
};

// 取得優先級顏色
export const getPriorityColor = (priority: 'high' | 'medium' | 'low') => {
  const colors = {
    high: { bg: '#fee', color: '#c53030', border: '#feb2b2' },
    medium: { bg: '#fffaf0', color: '#dd6b20', border: '#fbd38d' },
    low: { bg: '#e6fffa', color: '#2c7a7b', border: '#81e6d9' }
  };
  return colors[priority];
};

// 取得任務顏色
export const getTaskColor = (taskName: string) => {
  const colors: Record<string, string> = {
    '註冊功能': 'linear-gradient(135deg, #667eea, #764ba2)',
    '肌膚檢測功能': 'linear-gradient(135deg, #f093fb, #f5576c)',
    '電商功能': 'linear-gradient(135deg, #4facfe, #00f2fe)',
    '預約功能': 'linear-gradient(135deg, #43e97b, #38f9d7)'
  };
  return colors[taskName] || 'linear-gradient(135deg, #667eea, #764ba2)';
};

// 取得角色顏色
export const getRoleColor = (role: string) => {
  const colors: Record<string, string> = {
    'UI': '#ff6b6b',
    'UX': '#4ecdc4',
    '前端': '#667eea',
    '後端': '#45b7d1',
    '系統架構師': '#f7b731',
    'AI工程師': '#5f27cd'
  };
  return colors[role] || '#667eea';
};

// 生成唯一ID
export const generateId = (): string => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
}; 